import styles from './Stepper.module.css'

export interface StepperStep {
  label: string
}

export interface StepperProps {
  steps: StepperStep[]
  activeStep: number
  /** When provided, completed steps become clickable to jump back. */
  onStepClick?: (index: number) => void
  /**
   * Optional predicate to decide which steps are clickable. When provided it
   * overrides the default "completed steps only" rule — return true to make a
   * step (including upcoming ones) clickable. The active step is never clickable.
   */
  isStepClickable?: (index: number) => boolean
  className?: string
}

export function Stepper({ steps, activeStep, onStepClick, isStepClickable, className }: StepperProps) {
  const classes = [styles.stepper, className].filter(Boolean).join(' ')

  return (
    <nav className={classes} aria-label="Progress">
      <ol className={styles.list}>
        {steps.map((step, index) => {
          const state =
            index < activeStep ? 'completed' : index === activeStep ? 'active' : 'upcoming'

          const clickable = !!onStepClick && index !== activeStep &&
            (isStepClickable ? isStepClickable(index) : state === 'completed')

          const itemClasses = [
            styles.item,
            state === 'completed' && styles.completed,
            state === 'active' && styles.active,
            clickable && styles.clickable,
          ].filter(Boolean).join(' ')

          const indicator = (
            <>
              <div className={styles.indicator}>
                {state === 'completed' ? (
                  <svg className={styles.checkIcon} viewBox="0 0 16 16" fill="none">
                    <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <span className={styles.number}>{index + 1}</span>
                )}
              </div>
              <span className={styles.label}>{step.label}</span>
            </>
          )

          return (
            <li key={index} className={itemClasses}>
              {clickable ? (
                <button type="button" className={styles.stepButton} onClick={() => onStepClick(index)}>
                  {indicator}
                </button>
              ) : (
                indicator
              )}
              {index < steps.length - 1 && <div className={styles.connector} />}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
